/**
 * Canonical scan-status resolution, shared by every surface that renders a scan.
 *
 * Extracted for VERA-476 — App.Scans. The logic below already existed, hand-rolled,
 * inside `apps/platform/.../CertificatePage/components/Scans/Scans.tsx` plus
 * `apps/platform/src/hooks/api/useCertificateScanRecords.ts`. `apps/app`'s Scans tab
 * needed the same rules, which would have been the SECOND hand-roll — so it lives here
 * instead (working-conventions §36).
 *
 * `apps/platform`'s scan surfaces were migrated onto this module in VRG-491 — the last two
 * holdouts were the map hover tooltips in `CertificatePage/.../Scans.tsx` and analytics'
 * `ScansByLocation.tsx`, which each kept a private table of pill labels and colours and had
 * both drifted the SAME way: still painting `tampered` error-red and, in one case, still
 * labelling it "Tampered", after this module had reframed it as a neutral "Opened".
 *
 * That is the standing argument for keeping every scan-status decision here: a call site
 * that copies these values does not copy the next correction to them.
 *
 * Row shapes differ per caller (the action-log payload uses `type`/`metadata`, the
 * `/records` payload uses `invalid`/`tampered`), so the predicates read both.
 */

/** Minimal structural shape these resolvers need. Deliberately loose: callers pass
 *  action-log rows, `/records` rows, and merged render-token events. */
export interface ScanStatusInput {
  kind?: string | null;
  render_reason?: string | null;
  type?: string | null;
  invalid?: boolean | null;
  tampered?: boolean | null;
  metadata?: { tampered?: boolean | null } | null;
  /**
   * VRG-252 — what the scanned tag's family can prove AT ALL, independent of whether this tap
   * passed. Derived server-side from the tag family and sent instead of it (the chip family is
   * internal fulfilment detail and this resolver renders on the consumer app).
   *
   * `'static'` is what makes a row that is NEITHER verified NOR invalid legible. Without it such a
   * row falls through to `verified` below and is silently reported as confirmed genuine — the
   * exact overstatement VRG-252 exists to prevent, arriving in the brand's own scan log.
   */
  assurance?: 'cryptographic' | 'static' | null;
}

export type ScanStatusKey =
  | 'verified'
  | 'connected'
  | 'invalid'
  | 'tampered'
  | 'link_suspicious'
  | 'link_stale'
  | 'link_invalid';

export type ScanStatusTone = 'neutral' | 'warning' | 'error' | 'danger';

/**
 * Chip ramp: pale fill, mid-tone border, deep text — the same three-tone structure the
 * consumer app already uses for certificate statuses (`displayStatusColorMapper`,
 * established in VERA-315), reusing its exact values where a status maps onto one.
 *
 * Deliberately NOT `alpha(hue)` + hue-as-text. That put a mid-tone (#D97706) on a pale
 * fill, which is noticeably less legible than the 900-level text 315 uses, and it made
 * the scan chips a different family from the certificate chips one screen away.
 */
export interface ScanStatusChipRamp {
  /** Pale background. */
  fill: string;
  /** Mid-tone border — the colour signal. */
  border: string;
  /** Deep, high-contrast label. */
  text: string;
}

/*
 * DARK ramps for the MAP-TOOLTIP pill (`chipDark` below, read via `getScanTooltipPill`).
 *
 * A second ramp rather than a reuse of `chip`, because the two sit on opposite backgrounds
 * and the light ramp is unreadable on the dark one: `invalid`'s chip is `transparent` fill
 * with `#27272A` text, which on a dark map bubble is near-black on near-black. The map
 * tooltip is an HTML STRING injected into a Google Maps info window, so it cannot inherit
 * MUI theming the way a real chip does — it can only be handed literal colours.
 *
 * Structure: translucent tint + mid-tone translucent border + a LIGHT label, which is the
 * inverse of the light ramp's pale-fill/deep-text and the reason it needs its own values.
 */
const DARK_NEUTRAL: ScanStatusChipRamp = {
  fill: 'rgba(113,113,122,0.20)',
  border: 'rgba(113,113,122,0.45)',
  text: '#D4D4D8',
};
const DARK_ERROR: ScanStatusChipRamp = {
  fill: 'rgba(220,38,38,0.20)',
  border: 'rgba(220,38,38,0.45)',
  text: '#F1A9A9',
};
const DARK_PINK: ScanStatusChipRamp = {
  fill: 'rgba(219,39,119,0.20)',
  border: 'rgba(219,39,119,0.45)',
  text: '#F5A8CC',
};

/** Icon names a scan status can ask for. Mapped to real components by each app. */
export type ScanStatusIcon =
  | 'shield-alert'
  // VERA-260 — the CONFIRMED-genuine mark. Added for the analytics map legend, whose rows
  // are toggle pills rather than row chips, so unlike a list row it has to draw `verified`
  // as something. Naming it here rather than in that legend keeps the rule this module
  // already states for the other five: the resolver decides the state AND names its glyph,
  // and each app maps the name to a component (§44). It changes nothing existing —
  // `verified` is `showChip: false`, so no surface that reads `showChip` renders it.
  | 'shield-check'
  | 'shield-off'
  | 'link-2-off'
  | 'clock-alert'
  | 'lock-open'
  // VRG-252 — the Connect chip. The NFC mark names the EVENT (a tag was tapped) rather than
  // grading it, which is the same reasoning that put this glyph on the app's authenticity badge:
  // a shield would claim or deny authenticity, and this state does neither.
  | 'nfc';

export interface ScanStatusMeta {
  key: ScanStatusKey;
  label: string;
  /**
   * Saturated hue for the MAP MARKER. Distinct from the chip ramp on purpose: a pin is a
   * small dot on photographic tiles and needs the vivid mid-tone, whereas a chip needs
   * the pale/deep pairing to stay legible as text.
   */
  color: string;
  /** Fill / border / text for the status chip. */
  chip: ScanStatusChipRamp;
  /**
   * The same three tones for a DARK surface — today the map's HTML-string hover tooltip,
   * read through `getScanTooltipPill`.
   *
   * Exists because two platform surfaces (`CertificatePage/.../Scans.tsx` and the analytics
   * `ScansByLocation.tsx`) each hand-rolled this table, and both had drifted from the
   * resolver in the SAME direction: they still painted `tampered` error-red long after this
   * module reframed it as neutral "Opened". One of them even labelled it "Tampered" while
   * its own filter option in the same file read "Opened".
   *
   * So it is authored HERE rather than at the call sites — the third copy would have drifted
   * too (§36). A surface that needs a dark treatment asks the resolver for it.
   */
  chipDark: ScanStatusChipRamp;
  /**
   * Leading glyph for the chip, as a NAME rather than a component: this module is a
   * framework-free resolver shared by two apps, so it must not pull in an icon library.
   * Each app maps the name to its own icon component.
   *
   * Load-bearing, not decoration (VERA-415 / VERA-908): Invalid and Tampered are both
   * error-red, so the icon plus the label is what tells them apart.
   */
  icon?: ScanStatusIcon;
  /**
   * The `docs/glossary.json` entry that DEFINES this state, as an id rather than a
   * sentence — exactly like `icon` above names a glyph it cannot import.
   *
   * The resolver is where the state is decided, so it is also where the state's
   * definition should be looked up from: a surface renders
   * `<InfoTooltip term={meta.glossaryId} />` and the wording arrives from the one
   * place that owns it (§44). Before this, the six definitions lived in an explainer
   * modal at the bottom of the Scans tab, which meant the only way to learn what a
   * badge meant was to notice a detached link somewhere else on the page.
   *
   * `description` below stays as the plain-language fallback for surfaces with no
   * glossary access (apps/app) — the two must be kept saying the same thing, which is
   * why the glossary entries were seeded FROM these strings rather than rewritten.
   */
  glossaryId?: string;
  /**
   * How alarming this outcome is, 0 = fine. Used to pick which member of a STACK the
   * pin colours itself by (and which one pulses when selected): several scans share one
   * point, so the pin must speak for the worst of them or a clean scan sitting first in
   * the array would hide an invalid one underneath it.
   */
  severity: number;
  /**
   * Plain-language explanation of what this outcome MEANS, for the chip tooltip and the
   * "scan states" explainer. Lives on the resolver so the tooltip, the modal and the
   * docs can't drift from each other or from the rules that produce the state.
   *
   * Says what happened in the world, not what the code checked. The load-bearing
   * distinction for a reader is physical-vs-link: "Invalid" means someone tapped a tag
   * and it failed; the link states mean nobody tapped anything.
   */
  description: string;
  tone: ScanStatusTone;
  /**
   * Whether this status earns a chip in a list row.
   *
   * FALSE for `verified` — it is the expected, overwhelmingly common outcome, so a
   * chip on every row carries no information and drowns the exceptions. Absence of a
   * chip IS the verified signal. (The platform reached the same conclusion
   * independently: "Valid is unbadged (the expected state — a 'Valid' label is noise)".)
   */
  showChip: boolean;
}

/**
 * True when the row is a render-token (`vt`) verification EVENT rather than a physical
 * tag scan (VRG-106). These come from `public_verify_logs`, never from a tap, and can
 * never carry lat/lng or city — the fingerprinting middleware resolves country only.
 */
export const isRenderResolve = (scan?: ScanStatusInput | null): boolean =>
  scan?.kind === 'render_resolve';

/** True for a physical scan that failed verification. */
export const isInvalidScan = (scan?: ScanStatusInput | null): boolean =>
  Boolean(scan?.invalid || scan?.type === 'invalid_scan');

/** True when the tag itself reported tamper evidence.
 *
 * ⚠️ This is a BOOLEAN answer to a THREE-state question — deliberately, and it is correct
 * only for callers asking "is this one opened?" (sorting, the `tampered` status branch).
 * It collapses "the tag checked and the seal is intact" together with "the tag has no
 * tamper loop and could not check", because for those callers both mean "not opened".
 *
 * A caller that wants to SAY something about the seal — a label, a filter bucket, a chip —
 * must NOT use this. Use `sealState` below, which keeps the three states apart. See its
 * docblock for why the difference is load-bearing rather than pedantic.
 */
export const isTamperedScan = (scan?: ScanStatusInput | null): boolean =>
  Boolean(scan?.tampered || scan?.metadata?.tampered);

/**
 * What the tag actually reported about its tamper seal — three states, not two.
 *
 * `scans.tampered` is a NULLABLE boolean (`apps/api/src/database/schema/tables.ts`, no
 * `.notNull()`), and the null is NOT a rounding error or a backfill gap. It is a distinct,
 * and by far the most common, fact about the world:
 *
 * | value     | means                                              | local DB, 2026-08-31 |
 * |-----------|----------------------------------------------------|----------------------|
 * | `true`    | the tag reported its seal was opened               | 3 rows (0.05%)       |
 * | `false`   | the tag CHECKED and reports the seal intact        | 423 rows (6.56%)     |
 * | **NULL**  | **the tag has NO tamper loop — it CANNOT report**   | **6027 rows (93.4%)**|
 *
 * (Measured: `SELECT tampered, count(*) FROM scans GROUP BY 1`.)
 *
 * **Why a boolean here is a provenance overclaim.** Collapsing NULL into `false` makes the
 * product assert an intact seal on 93% of scans where nothing was ever checked. On a
 * provenance product that is the worst available failure — it is the same overstatement as
 * a false tamper alarm, pointed the other way, and it is harder to notice because it reads
 * as reassuring. The API already draws this distinction and always has: the tamper-latch
 * query in `certificate.drizzle.repository.ts` filters `WHERE "tampered" IS NOT NULL`
 * precisely so an unsupported tag read cannot clear a real tamper. This resolver is the
 * client-side half of that same rule.
 *
 * `unknown` covers null AND undefined in BOTH fields, because absence and explicit null are
 * the same fact to a reader — the tag said nothing. That matters more than it sounds: the
 * platform's Scans tab is action-log-backed and carries no `tampered` field at all, so every
 * row there is `unknown` by absence rather than by null.
 *
 * A surface rendering this should show `unknown` as no claim at all — not a neutral
 * "unopened" chip, which is still an assertion — and should keep it out of any
 * opened-vs-unopened filter bucket, the same way `isRenderResolve` rows are excluded from
 * the physical scan axes (VRG-106).
 */
export type SealState = 'opened' | 'unopened' | 'unknown';

export const sealState = (scan?: ScanStatusInput | null): SealState => {
  const top = scan?.tampered;
  const meta = scan?.metadata?.tampered;
  // Checked before the `false` branch: a `true` from EITHER field wins, so a row carrying
  // an explicit `false` alongside a `true` can never be reported as intact.
  if (top === true || meta === true) return 'opened';
  // Strict `=== false` is the whole point — `undefined` and `null` must fall through to
  // `unknown` rather than being swept up by a falsy check.
  if (top === false || meta === false) return 'unopened';
  return 'unknown';
};

/**
 * Render-token failure detail. Forged / wrong-purpose / cross-cert replay are
 * SUSPICIOUS (someone is probing); an expired token is the benign re-view case.
 */
export const renderResolveMeta = (reason?: string | null): ScanStatusMeta => {
  switch (reason) {
    case 'vt_bad_signature':
    case 'vt_wrong_purpose':
    case 'vt_cert_mismatch':
      return {
        key: 'link_suspicious',
        glossaryId: 'scan-status-suspicious-link',
        description: 'Someone opened this certificate\u2019s link using a forged or mismatched token. No tag was tapped, so this is probing rather than a scan.',
        label: 'Suspicious link',
      severity: 2,
        // MAP PIN colour, deliberately the same red as `invalid`. The map runs a
        // THREE-tone ramp on purpose — purple "fine", grey "couldn't confirm", red "something
        // is wrong" — because a pin has no text, so every extra hue is one more thing the
        // reader has to decode from a legend. Pink here made "suspicious" its own colour that
        // nobody could rank against red at a glance. Grey would have been worse: grey means
        // the ABSENCE of information, and a forged token is a positive signal that someone is
        // probing. So it ranks with the alerts. The finer six-state distinction is NOT lost —
        // it survives in `chip` + `label` + `description` below, where there is text to carry it.
        color: '#DC2626',
        // Chip keeps its own pink ramp (the app's `gift_received` hue): chips are labelled, so
        // they can stay granular where the pin cannot.
        chip: { fill: '#FCE7F1', border: '#FBCFE5', text: '#83184D' },
        chipDark: DARK_PINK,
        icon: 'shield-alert',
        tone: 'danger',
        showChip: true,
      };
    case 'vt_expired':
      // TODO(VRG-211 / TD-0XX): link_stale and link_invalid render identical neutral
      // chips today and are candidates to merge into one label once VERA-628 merges
      // (its statusChipSx + TagOpenedBadge already claim "Opened" for a DIFFERENT fact
      // — the tag's physical tamper seal — so the merged label here must NOT reuse that
      // word). See VRG-211-App.Scans.Opened.Chip.Unification for the full writeup.
      return {
        key: 'link_stale',
        glossaryId: 'scan-status-stale-link',
        description: 'An expired certificate link was reopened. Usually harmless, such as an old link in a message or a stale browser tab.',
        label: 'Stale link',
      severity: 1,
        color: '#71717A',
        // Neutral zinc ramp, same 50/200/800 structure as the certificate statuses.
        chip: { fill: '#F4F4F5', border: '#E4E4E7', text: '#27272A' },
        chipDark: DARK_NEUTRAL,
        icon: 'clock-alert',
        tone: 'neutral',
        showChip: true,
      };
    default:
      return {
        key: 'link_invalid',
        glossaryId: 'scan-status-invalid-link',
        description: 'A malformed certificate link was opened. No tag was tapped.',
        label: 'Invalid link',
      severity: 1,
        color: '#71717A',
        chip: { fill: '#F4F4F5', border: '#E4E4E7', text: '#27272A' },
        chipDark: DARK_NEUTRAL,
        icon: 'link-2-off',
        tone: 'neutral',
        showChip: true,
      };
  }
};

/**
 * Resolve any scan-ish row to its display status.
 *
 * Order matters: a render-token event is never a physical scan, so it must be checked
 * FIRST or it would fall through to the verified branch and be reported as a genuine
 * successful scan.
 */
export const getScanStatus = (
  scan?: ScanStatusInput | null,
  opts?: { isDark?: boolean },
): ScanStatusMeta => {
  if (isRenderResolve(scan)) return renderResolveMeta(scan?.render_reason);
  // INVALID is checked BEFORE tampered (VERA-628). A failed verification is the primary
  // signal about a scan; an open seal is orthogonal metadata that can accompany a
  // perfectly valid one. The old order reported a valid-but-opened scan as `tampered`,
  // which is the conflation this whole reframe exists to remove.
  if (isInvalidScan(scan)) {
    return {
      key: 'invalid',
      glossaryId: 'scan-status-invalid',
      description: 'A tag was tapped but did not verify. It may be a copy of a real tag, or a genuine tag that is damaged or misprogrammed.',
      label: 'Invalid',
      severity: 3,
      // Its OWN error tone, deliberately a notch BELOW Stolen and Tampered rather than
      // identical to them. Those two are confirmed states — an owner has declared the
      // item stolen, or the tag itself reported a physical breach. An invalid scan is
      // EVIDENCE: possibly a counterfeit, possibly a cloned link, possibly nothing.
      // Ranking evidence equal to a confirmed state overstates it, and three identical
      // red pills across two different axes (certificate status vs scan outcome) made
      // the page unreadable.
      //
      // So: transparent fill + red border, with the red carried by the ICON rather than
      // a filled block. Reads as an alert without competing with the solid red of a
      // Stolen certificate. The label stays near-black for legibility (a red-on-pale
      // label at 12px is the first thing to fail a contrast check).
      color: '#DC2626',
      chip: { fill: 'transparent', border: '#FECACA', text: '#27272A' },
      // The one status that CANNOT reuse its light ramp on a dark surface: `transparent`
      // fill + `#27272A` text is near-black on near-black. A real tint is required here.
      chipDark: DARK_ERROR,
      // Shield-with-exclamation, exactly as Figma marks an invalid scan. This is the
      // ONLY thing distinguishing the chip from Tampered now that both are red, so it
      // is required, not ornamental.
      icon: 'shield-alert',
      tone: 'error',
      showChip: true,
    };
  }
  if (isTamperedScan(scan)) {
    return {
      key: 'tampered',
      glossaryId: 'scan-status-opened',
      label: 'Opened',
      description:
        'The tag reports its seal was opened. On a closure/seal tag this is the expected outcome once the owner opens the item, so it is a fact about the seal rather than a problem with the scan.',
      // Severity 0, same as `verified`: severity picks which member of a stacked pin
      // speaks for the group, and an opened seal must NOT outrank a genuinely invalid
      // scan sharing that point. It was 4 (the top tier) when this was framed as an alert.
      severity: 0,
      // An open padlock, not a struck-through shield: this is a state, not a breach.
      icon: 'lock-open',
      // VERA-628: the MARKER stays the normal verified purple. A scan that verified
      // successfully and reported an open seal IS a successful scan — the seal state is
      // orthogonal to whether verification passed. Colouring the pin red said "this scan
      // failed", which was never true. Only `invalid` (amber) and the render-token
      // failures earn a non-verified pin.
      color: opts?.isDark ? '#8F68EA' : '#7F48DF',
      // Neutral zinc, matching the app's "Opened" badge — a fact about the seal, not an
      // alert. Was the `stolen` ramp, which made a routine open look like a theft report.
      chip: { fill: '#F4F4F5', border: '#E4E4E7', text: '#27272A' },
      // Neutral on dark too. This is the value the two map tooltips were missing: both
      // kept an error-red pill here, so an "Opened" scan was still being reported as an
      // alert on the map long after the chip beside it went neutral.
      chipDark: DARK_NEUTRAL,
      tone: 'neutral',
      showChip: true,
    };
  }
  // VRG-252 — a Connect tap: the tag was read and resolved, and its silicon carries no proof to
  // check. Placed LAST, immediately before `verified`, so it changes nothing about the three
  // states above it: an invalid or tampered Connect scan is still reported as invalid or tampered,
  // because those are facts about THIS tap and this is a fact about the tag's family.
  //
  // Reaching `verified` for such a row would have been the failure — the brand's own scan log
  // would have said "confirmed genuine" about a tap that confirmed nothing of the sort.
  if (scan?.assurance === 'static') {
    return {
      key: 'connected',
      glossaryId: 'scan-status-connected',
      label: 'Connected',
      description:
        'The tag was tapped and opened this certificate. This tag is sealed so it cannot be rewritten, but it carries no per-tap proof, so a scan cannot tell it apart from a copy of its address.',
      // Severity 0, like `verified` and `tampered`. This is the EXPECTED outcome for a Connect
      // tag, not a problem — ranking it above a genuinely invalid scan sharing a map point would
      // let a routine tap speak for a stack that contains a real failure.
      severity: 0,
      // The same brand purple as verified: the pin answers "did a real tap happen here", and one
      // did. The chip carries the distinction, where there is room to say what kind.
      color: opts?.isDark ? '#8F68EA' : '#7F48DF',
      // Neutral zinc — a fact about the tag, not an alert. Matches `tampered`'s ramp for the same
      // reason: both are states, neither is a breach.
      chip: { fill: '#F4F4F5', border: '#E4E4E7', text: '#27272A' },
      chipDark: DARK_NEUTRAL,
      icon: 'nfc',
      tone: 'neutral',
      // SHOWN, unlike `verified`. The argument for hiding the verified chip is that it is the
      // overwhelmingly common outcome so its absence carries the signal — which is exactly why
      // this one must be visible: if both were unbadged, the absence of a chip would mean two
      // different things and the weaker one would be read as the stronger.
      showChip: true,
    };
  }

  return {
    key: 'verified',
    glossaryId: 'scan-status-verified',
    label: 'Verified',
    description: 'The tag was tapped and confirmed genuine. This is the normal outcome, so it carries no badge.',
    severity: 0,
    // Brand purple — an "authenticated" dot that stays cool so the warm red/amber
    // alerts still pop. Brighter on a dark map, deeper on a pale one.
    color: opts?.isDark ? '#8F68EA' : '#7F48DF',
    // A shield WITH A CHECK, the positive counterpart to `invalid`'s shield-with-alert.
    // Only surfaces that draw verified explicitly (the analytics map legend) read it; a
    // list row still shows nothing, because `showChip` is false and absence IS the
    // verified signal there.
    icon: 'shield-check',
    /**
     * ⚠️ NEVER RENDERED AS A CHIP (`showChip` is false) — present so the type stays total.
     *
     * It is also NOT the colour to reach for when a surface has to draw verified anyway.
     * `apps/platform`'s analytics scan-status FILTER did exactly that and painted its
     * "Verified" pill this green, which no other surface uses and which hovered as a grey
     * (`alpha('#14532B', 0.08)`). Use `color` above — the brand purple the map pin, the
     * map legend and the filter chip now all share. See `SCAN_STATUS_OPTIONS` in
     * `apps/platform/src/components/analytics/analyticsFilterFields.ts`.
     */
    chip: { fill: '#DCFCE8', border: '#BBF7D1', text: '#14532B' },
    // Also never rendered — `showChip` is false, and `getScanTooltipPill` returns null for
    // it. Present so the type stays total, same as `chip` above.
    chipDark: DARK_NEUTRAL,
    tone: 'neutral',
    showChip: false,
  };
};

/**
 * The map hover tooltip's status pill, or `null` when this outcome earns none.
 *
 * The map tooltip is built as an HTML STRING (Google Maps info windows take markup, not
 * React), so it cannot render a `ScanStatusChip` and cannot inherit theme colours — it can
 * only be handed literal values. This is the seam where that happens, so the label and the
 * three tones still come from ONE place.
 *
 * ⚠️ Returns null for `verified` via `showChip`, which is the same rule the list rows follow:
 * verified is the overwhelmingly common outcome, so absence of a pill IS the verified signal
 * and a pill on every pin would carry no information (see `showChip`'s own note).
 *
 * Added for VRG-491. Before it, `CertificatePage/.../Scans.tsx` and analytics'
 * `ScansByLocation.tsx` each carried a private `Record<string, {...}>` of these values, and
 * both still painted `tampered` error-red — the overclaim this resolver had already dropped.
 * Deriving them here means a future status is drawn correctly by both, or by neither, but
 * never differently by each.
 */
export const getScanTooltipPill = (
  meta?: ScanStatusMeta | null,
): ({ label: string } & ScanStatusChipRamp) | null =>
  meta && meta.showChip ? { label: meta.label, ...meta.chipDark } : null;

/** Marker/pin colour for a scan, matching the status palette above. */
export const getScanMarkerColor = (scan?: ScanStatusInput | null, isDark = false): string =>
  getScanStatus(scan, { isDark }).color;

/**
 * Metadata a scan row can carry for location. Deliberately loose — the same jsonb blob
 * is written by three different paths with different shapes.
 */
export interface ScanLocationMetadata {
  /** Google reverse-geocoded street address. Best available label. */
  formatted_address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /**
   * DANGER: this is an OBJECT `{latitude, longitude, accuracy}` in real data
   * (`update-scan-location` writes the raw request body), but a STRING like
   * "Geneva, Switzerland" in seed data. Never call string methods on it directly.
   */
  location?:
    | string
    // `lat`/`lng` are not hypothetical: `UpdateScanLocationUseCase` reads
    // `coords.lat ?? coords.latitude` off the request body and then stores that body
    // VERBATIM as `metadata.location`, so whichever spelling the caller sent is the
    // spelling on the row. A reader that accepts fewer spellings than the writer does
    // is a bug waiting for the first client that picks the other one.
    | { latitude?: number; longitude?: number; lat?: number; lng?: number; long?: number; accuracy?: number }
    | null;
  fingerprint?: { geoip?: { country?: string | null } | null } | null;
  /** Legacy/geoip rows abbreviate these. Read, never written by current code. */
  lat?: number | null;
  lng?: number | null;
  long?: number | null;
}

/** A scan row, as far as coordinate extraction is concerned. */
export interface ScanCoordsInput {
  /** Structured columns on the `scans` TABLE (VRG-161 writes these). */
  geo_lat?: number | string | null;
  geo_lng?: number | string | null;
  /** Flat shape carried by a merged `/records` row. */
  lat?: number | string | null;
  lng?: number | string | null;
  metadata?: ScanLocationMetadata | null;
}

const finiteOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Plottable coordinates for a scan row, or `null` when it has none.
 *
 * THE WHOLE POINT OF THIS FUNCTION is the last branch — `metadata.location` as an
 * OBJECT. That is the canonical shape written when a visitor submits their location on
 * the public certificate page (`LogPublicAccessUseCase` builds
 * `metadata.location = {latitude, longitude, accuracy}`, and
 * `UpdateScanLocationUseCase` assigns the raw request body the same way). It is NOT a
 * fallback or a legacy shape: it is what current production code writes, and it is the
 * one shape every hand-rolled reader kept forgetting.
 *
 * The cost of forgetting it was a silent, total map failure: the scan carried a
 * reverse-geocoded "Miami, Florida, United States" that the LIST rendered happily off
 * `metadata.city/state/country`, while the MAP — reading only the top-level spellings —
 * found zero plottable points and displayed its "no scans" empty state. List and map
 * disagreed about whether the data existed, and the map's version looked like broken
 * tracking. `ScanItem` had already been fixed for this in VRG-165; the map had not, and
 * nothing tied the two readers together. Hence one exported extractor (§36) rather than
 * a third copy of the `??` chain.
 *
 * Order is best-structured-first, but every branch is the same fact, so it only matters
 * when a row carries two spellings that disagree.
 */
export const getScanCoords = (
  scan?: ScanCoordsInput | null,
): { lat: number; lng: number } | null => {
  if (!scan) return null;
  const metadata = scan.metadata ?? null;
  const nested =
    metadata && typeof metadata.location === 'object' && metadata.location !== null
      ? metadata.location
      : null;

  const lat =
    finiteOrNull(scan.geo_lat) ??
    finiteOrNull(scan.lat) ??
    finiteOrNull(metadata?.latitude) ??
    finiteOrNull(metadata?.lat) ??
    finiteOrNull(nested?.latitude) ??
    finiteOrNull(nested?.lat);
  const lng =
    finiteOrNull(scan.geo_lng) ??
    finiteOrNull(scan.lng) ??
    finiteOrNull(metadata?.longitude) ??
    finiteOrNull(metadata?.long) ??
    finiteOrNull(metadata?.lng) ??
    finiteOrNull(nested?.longitude) ??
    finiteOrNull(nested?.lng) ??
    finiteOrNull(nested?.long);

  // Both or neither. A lone latitude cannot be placed on a map, and passing it through
  // as `{lat, lng: undefined}` puts a marker at the prime meridian.
  if (lat === null || lng === null) return null;
  return { lat, lng };
};

/**
 * Human-readable location for a scan row, best-available first:
 * reverse-geocoded address → "City, State, Country" → coarse geoip country → coords.
 *
 * Returns `null` when nothing is known, so callers choose their own empty copy.
 *
 * Exists because four call sites in apps/app treated `metadata.location` as a string.
 * It is only a string in SEED data; real rows store an object, so those sites rendered
 * "[object Object]" and threw on `.toLowerCase()` against production data (VERA-476).
 */
export const getScanLocationLabel = (
  metadata?: ScanLocationMetadata | null,
): string | null => {
  if (!metadata) return null;

  if (metadata.formatted_address) return metadata.formatted_address;

  // Drop the region when we have both a city and a country: "London, England, GB" is
  // three levels of geography where two answer the question. The region only earns its
  // place when one of the other two is missing.
  const hasCityAndCountry = Boolean(metadata.city?.trim() && metadata.country?.trim());

  // Dedupe case-insensitively: city-states repeat themselves ("Zurich, Zurich,
  // Switzerland", "Singapore, Singapore"), which reads like a bug.
  const seen = new Set<string>();
  const parts = [metadata.city, hasCityAndCountry ? null : metadata.state, metadata.country]
    .map(part => part?.trim())
    .filter((part): part is string => Boolean(part))
    .filter(part => {
      const key = part.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  if (parts.length) return parts.join(', ');

  // Only usable when it's the seed-shaped string.
  if (typeof metadata.location === 'string' && metadata.location.trim()) {
    return metadata.location;
  }

  const geoipCountry = metadata.fingerprint?.geoip?.country;
  if (geoipCountry) return geoipCountry;

  const lat = metadata.latitude ?? (typeof metadata.location === 'object' ? metadata.location?.latitude : null);
  const lng = metadata.longitude ?? (typeof metadata.location === 'object' ? metadata.location?.longitude : null);
  if (lat != null && lng != null) return `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;

  return null;
};

/** The status keys a filter UI can offer, in display order. */
export const SCAN_STATUS_FILTER_OPTIONS: Array<{ key: ScanStatusKey; label: string }> = [
  { key: 'verified', label: 'Verified' },
  { key: 'connected', label: 'Connected' },
  { key: 'invalid', label: 'Invalid' },
  { key: 'tampered', label: 'Opened' },
  { key: 'link_suspicious', label: 'Suspicious link' },
  { key: 'link_stale', label: 'Stale link' },
  { key: 'link_invalid', label: 'Invalid link' },
];
